import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  LinearProgress,
  Chip,
  SelectChangeEvent,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { useBanner } from "../context/BannerContext";
import { ImportLogEntry } from "../../integrations/arcol/arcolImport";
import { useImportActions } from "../hooks/useImportActions";
import { ArcolExport } from "../../integrations/arcol/types";
import { IMPORT_SUCCESS, arcolFileLoaded, importCompletedWithErrors } from "../constants";

interface Region {
  id: string;
  name: string;
}

const ImportPage: React.FC = () => {
  const { showBanner } = useBanner();
  const { loadJsonFile, getRegions, runImport } = useImportActions();
  const [arcolData, setArcolData] = useState<ArcolExport | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [importing, setImporting] = useState(false);
  const [progressStep, setProgressStep] = useState("");
  const [progressDetail, setProgressDetail] = useState("");
  const [importLog, setImportLog] = useState<ImportLogEntry[]>([]);
  const [resultProjectId, setResultProjectId] = useState<string | null>(null);

  const handleSelectFile = async () => {
    try {
      const data = await loadJsonFile();
      setArcolData(data);
      setFileName(data.project_name);
      setImportLog([]);
      setResultProjectId(null);
      showBanner(arcolFileLoaded(data.project_name, data.buildings.length), "success");

      // Load regions for the selector
      const regionList = await getRegions();
      setRegions(regionList);
      if (regionList.length > 0) {
        setSelectedRegion(regionList[0].id);
      }
    } catch (err) {
      showBanner(err instanceof Error ? err.message : "Failed to read file", "error");
    }
  };

  const handleImport = async () => {
    if (!arcolData || !selectedRegion) return;

    setImporting(true);
    setImportLog([]);
    setResultProjectId(null);

    try {
      const result = await runImport(arcolData, selectedRegion, (step, detail) => {
        setProgressStep(step);
        setProgressDetail(detail);
      });

      setImportLog(result.log);
      setResultProjectId(result.projectId);

      const errors = result.log.filter((e) => e.status === "error");
      if (errors.length === 0) {
        showBanner(IMPORT_SUCCESS, "success");
      } else {
        showBanner(importCompletedWithErrors(errors.length), "warn");
      }
    } catch (err) {
      showBanner(err instanceof Error ? err.message : "Import failed", "error");
    } finally {
      setImporting(false);
      setProgressStep("");
      setProgressDetail("");
    }
  };

  const totalFloors = arcolData
    ? arcolData.buildings.reduce((sum, b) => sum + b.floors.reduce((fs, f) => fs + (f.floor_count || 1), 0), 0)
    : 0;

  const uniqueAreaTypes = arcolData
    ? Array.from(new Set(arcolData.buildings.flatMap((b) => b.floors.flatMap((f) => f.areas.map((a) => a.type)))))
    : [];

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        Arcol Import
      </Typography>

      {/* File Selection */}
      <Button
        variant="outlined"
        startIcon={<FileUploadOutlinedIcon />}
        onClick={handleSelectFile}
        disabled={importing}
        fullWidth
        sx={{ height: "44px" }}
      >
        {fileName ? `Change File (${fileName})` : "Select Arcol JSON"}
      </Button>

      {/* Data Preview */}
      {arcolData && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>
            {arcolData.project_name}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            <Chip label={`${arcolData.buildings.length} buildings`} size="small" />
            <Chip label={`${totalFloors} floors`} size="small" />
            <Chip label={`${uniqueAreaTypes.length} area types`} size="small" />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Buildings: {arcolData.buildings.map((b) => b.name).join(", ")}
          </Typography>
        </Paper>
      )}

      {/* Region Selector */}
      {arcolData && regions.length > 0 && (
        <FormControl size="small" fullWidth>
          <InputLabel>Region</InputLabel>
          <Select
            value={selectedRegion}
            label="Region"
            onChange={(e: SelectChangeEvent) => setSelectedRegion(e.target.value)}
            disabled={importing}
          >
            {regions.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Import Button */}
      {arcolData && selectedRegion && (
        <Button
          variant="contained"
          startIcon={<PlayArrowOutlinedIcon />}
          onClick={handleImport}
          disabled={importing}
          fullWidth
          sx={{ height: "44px" }}
        >
          {importing ? "Importing..." : "Import to Ediphi"}
        </Button>
      )}

      {/* Progress */}
      {importing && (
        <Box>
          <LinearProgress sx={{ mb: 0.5 }} />
          <Typography variant="caption" color="text.secondary">
            {progressStep}: {progressDetail}
          </Typography>
        </Box>
      )}

      {/* Import Log */}
      {importLog.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 300, overflow: "auto" }}>
          <Typography variant="subtitle2" gutterBottom>
            Import Log
          </Typography>
          {importLog.map((entry, i) => (
            <Typography
              key={i}
              variant="caption"
              display="block"
              sx={{
                color:
                  entry.status === "error" ? "error.main" : entry.status === "skip" ? "warning.main" : "text.secondary",
                fontFamily: "monospace",
                lineHeight: 1.6,
              }}
            >
              [{entry.status.toUpperCase()}] {entry.step}: {entry.message}
            </Typography>
          ))}
        </Paper>
      )}

      {/* Result */}
      {resultProjectId && (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "success.light" }}>
          <Typography variant="subtitle2">Import Complete</Typography>
          <Typography variant="caption">Project ID: {resultProjectId}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ImportPage;
